import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs';
import { ViewportOverlayDirective } from '@/app/common/directives/viewport-overlay/viewport-overlay.directive';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { VideoResponseDto } from '@/app/common/general-types/videos.dto';
import { VideoThumbnailComponent } from '@/app/atoms/video-thumbnail/video-thumbnail.component';
import {
  AD_LICENSE_TERMS_URL,
  AdLicenseDto,
  AdLicenseMutationResponseDto,
  AdLicenseQuoteDto,
} from '@/app/common/general-types/ad-license.dto';
import { AdLicenseService } from '@/app/services/ad-license/ad-license.service';
import { WalletService } from '@/app/services/wallet/wallet.service';

type BuyAdLicenseStep = 'choose' | 'sent';

/** Icon glyph per right key; unknown keys fall back to the megaphone. */
const RIGHT_ICONS: Record<string, 'trending' | 'target'> = {
  boost: 'trending',
  dark_ads: 'target',
};

/**
 * Buy Ad License modal: explains the licence, shows the wallet impact and sends the request.
 * The price is reserved from the wallet on submit and released if the creator declines.
 */
@Component({
  selector: 'vs-buy-ad-license-modal',
  imports: [CurrencyPipe, VideoThumbnailComponent],
  hostDirectives: [ViewportOverlayDirective],
  templateUrl: './buy-ad-license-modal.html',
  styleUrl: './buy-ad-license-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'closeModal()'
  }
})
export class BuyAdLicenseModal implements OnInit {
  readonly video = input.required<VideoResponseDto>();
  readonly quote = input.required<AdLicenseQuoteDto>();
  readonly draftLabel = input<string>('Draft');
  readonly creatorFirstName = input<string>('The creator');

  readonly close = output<void>();
  readonly licenseRequested = output<AdLicenseDto>();

  readonly termsUrl = AD_LICENSE_TERMS_URL;

  readonly step = signal<BuyAdLicenseStep>('choose');
  readonly agreed = signal<boolean>(false);
  readonly submitting = signal<boolean>(false);
  readonly errorMessage = signal<string | null>(null);
  /** Set when the API answers 402, even if our cached balance looked high enough. */
  readonly serverInsufficientFunds = signal<boolean>(false);
  /** Balance returned by the purchase call, in major units. */
  readonly newBalance = signal<number | null>(null);

  private readonly adLicenseService = inject(AdLicenseService);
  private readonly walletService = inject(WalletService);
  private readonly currentUserService = inject(CurrentUserService);

  thumbnailUrl(): string | undefined {
    return this.video().thumbnailURL || undefined;
  }

  thumbnailCdnKey(): string | undefined {
    return this.video().thumbnailCdnKey || undefined;
  }

  readonly currencyCode = computed(() => (this.quote().currencyCode || 'gbp').toUpperCase());
  /** Brand price incl. VAT, in major units for the currency pipe. */
  readonly price = computed(() => this.quote().brandPrice / 100);
  readonly vat = computed(() => this.quote().vatAmount / 100);

  /** Wallet balance in major units, or null until the focussed brand's balance is known. */
  readonly walletBalance = computed<number | null>(() => {
    const brandId = this.currentUserService.focussedBrand?.id;
    const balance = this.walletService.availableBalance();
    if (balance === undefined || !brandId || this.walletService.balanceBusinessAccountId() !== brandId) {
      return null;
    }
    return balance;
  });

  readonly balanceAfter = computed<number | null>(() => {
    const balance = this.walletBalance();
    return balance === null ? null : balance - this.price();
  });

  readonly insufficientFunds = computed(() => {
    if (this.serverInsufficientFunds()) return true;
    const after = this.balanceAfter();
    return after !== null && after < 0;
  });

  readonly canSubmit = computed(() => this.agreed() && !this.submitting() && !this.insufficientFunds());

  readonly rights = computed(() =>
    this.quote().rights.map(right => ({ ...right, icon: RIGHT_ICONS[right.key] ?? 'megaphone' })),
  );

  ngOnInit(): void {
    const brandId = this.currentUserService.focussedBrand?.id;
    if (brandId) {
      this.walletService.getBalance(brandId).subscribe({
        error: (err: unknown) => console.warn('Could not refresh wallet balance', err),
      });
    }
  }

  toggleAgreed(): void {
    if (this.submitting()) return;
    this.agreed.update(value => !value);
  }

  /** Opening the terms must not toggle the checkbox row it sits in. */
  onTermsClick(event: Event): void {
    event.stopPropagation();
  }

  openTopUp(): void {
    const brand = this.currentUserService.focussedBrand;
    this.walletService.openTopUpDrawer(brand ? {
      businessAccountId: brand.id,
      currencyCode: brand.currencyCode,
      companyName: brand.companyName,
    } : null);
    this.closeModal();
  }

  submit(): void {
    if (!this.canSubmit()) return;
    this.submitting.set(true);
    this.errorMessage.set(null);

    this.adLicenseService
      .request({ videoId: this.video().id, termsAccepted: true })
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: (response: AdLicenseMutationResponseDto) => {
          this.newBalance.set(response.walletBalance.availableBalance / 100);
          this.step.set('sent');
          this.licenseRequested.emit(response.license);
          const brandId = this.currentUserService.focussedBrand?.id;
          if (brandId) {
            this.walletService.getBalance(brandId).subscribe({
              error: (err: unknown) => console.warn('Could not refresh wallet balance', err),
            });
          }
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 402) {
            this.serverInsufficientFunds.set(true);
            return;
          }
          this.errorMessage.set(this.describeError(err));
        },
      });
  }

  closeModal(): void {
    if (this.submitting()) return;
    this.close.emit();
  }

  private describeError(err: HttpErrorResponse): string {
    const code = (err.error as { code?: string } | null)?.code;
    switch (code) {
      case 'AD_LICENSE_EXISTS':
        return 'This video already has a licence request. Refresh the page to see it.';
      case 'VIDEO_NOT_APPROVED':
        return 'This draft needs to be approved before you can license it.';
      case 'AD_LICENSE_NOT_ELIGIBLE':
        return 'An Ad License is not available for this brief.';
      default:
        return 'We couldn’t send your licence request. Please try again.';
    }
  }
}
